import { Component, type ReactNode, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PropertyProvider } from './contexts/PropertyContext';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/LoginPage';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { PermissionGate } from './components/shared/PermissionGate';
import { PageErrorBoundary } from './components/shared/PageErrorBoundary';
import { Spinner, ToastProvider } from './components/shared';
import { ScrapingProvider } from './contexts/ScrapingContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { devError } from './utils/dev-log';
import { isFirestoreAssertionError } from './utils/firestore-errors';

function withBoundary(pageName: string, children: ReactNode) {
  return <PageErrorBoundary pageName={pageName}>{children}</PageErrorBoundary>;
}

// ---------------------------------------------------------------------------
// Lazy-loaded page components (route-based code splitting)
// ---------------------------------------------------------------------------

const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const LiveSessionsPage = lazy(() => import('./pages/LiveSessionsPage').then(m => ({ default: m.LiveSessionsPage })));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })));
const HandoverQueuePage = lazy(() => import('./pages/HandoverQueuePage').then(m => ({ default: m.HandoverQueuePage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const OrganizationsPage = lazy(() => import('./pages/OrganizationsPage').then(m => ({ default: m.OrganizationsPage })));
const PropertiesPage = lazy(() => import('./pages/PropertiesPage').then(m => ({ default: m.PropertiesPage })));
const PropertySettingsPage = lazy(() => import('./pages/PropertySettingsPage').then(m => ({ default: m.PropertySettingsPage })));
const WidgetInstallPage = lazy(() => import('./pages/WidgetInstallPage').then(m => ({ default: m.WidgetInstallPage })));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage').then(m => ({ default: m.DesignSystemPage })));
const GuestProfilesPage = lazy(() => import('./pages/GuestProfilesPage').then(m => ({ default: m.GuestProfilesPage })));
const AutomationPage = lazy(() => import('./pages/AutomationPage').then(m => ({ default: m.AutomationPage })));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage').then(m => ({ default: m.CampaignsPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const BillingPage = lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const PlatformBillingPage = lazy(() => import('./pages/PlatformBillingPage').then(m => ({ default: m.PlatformBillingPage })));
const BrandsPage = lazy(() => import('./pages/BrandsPage').then(m => ({ default: m.BrandsPage })));
const TestChatPage = lazy(() => import('./pages/TestChatPage').then(m => ({ default: m.TestChatPage })));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const TeamPage = lazy(() => import('./pages/TeamPage').then(m => ({ default: m.TeamPage })));
const PlatformHealthPage = lazy(() => import('./pages/PlatformHealthPage').then(m => ({ default: m.PlatformHealthPage })));
const AIOpsPage = lazy(() => import('./pages/AIOpsPage').then(m => ({ default: m.AIOpsPage })));
const CustomerHealthDetailPage = lazy(() => import('./pages/CustomerHealthDetailPage').then(m => ({ default: m.CustomerHealthDetailPage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const SecuritySettingsPage = lazy(() => import('./pages/SecuritySettingsPage').then(m => ({ default: m.SecuritySettingsPage })));
const GettingStartedPage = lazy(() => import('./pages/GettingStartedPage').then(m => ({ default: m.GettingStartedPage })));
const AccountActivationPage = lazy(() => import('./pages/AccountActivationPage').then(m => ({ default: m.AccountActivationPage })));
const SalesProvisioningPage = lazy(() => import('./pages/SalesProvisioningPage').then(m => ({ default: m.SalesProvisioningPage })));
const AuditHistoryPage = lazy(() => import('./pages/AuditHistoryPage').then(m => ({ default: m.AuditHistoryPage })));
const InternalAccessPage = lazy(() => import('./pages/InternalAccessPage').then(m => ({ default: m.InternalAccessPage })));
const OrganizationStructurePage = lazy(() => import('./pages/OrganizationStructurePage').then(m => ({ default: m.OrganizationStructurePage })));
const GlobalGuestsPage = lazy(() => import('./pages/GlobalGuestsPage').then(m => ({ default: m.GlobalGuestsPage })));
const Recover2FAPage = lazy(() => import('./pages/Recover2FAPage').then(m => ({ default: m.Recover2FAPage })));

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isFirestoreError: boolean;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null, isFirestoreError: false };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error, isFirestoreError: isFirestoreAssertionError(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (this.state.isFirestoreError) {
      devError('[AppErrorBoundary] Firestore watch stream assertion error — reload required', error.message);
      return;
    }
    devError('[AppErrorBoundary]', error, info.componentStack);

    // Report to backend (fire-and-forget)
    const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003';
    fetch(`${apiUrl}/v1/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'admin',
        message: error.message,
        stack: error.stack,
        context: {
          url: window.location.href,
          browser: navigator.userAgent,
          componentStack: info.componentStack,
        },
      }),
    }).catch((err) => devError('Error report dispatch failed', err));
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-50 p-6">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-surface-900 mb-2">
              {this.state.isFirestoreError ? 'Connection interrupted' : 'Something went wrong'}
            </h1>
            <p className="text-surface-600 mb-6">
              {this.state.isFirestoreError
                ? 'The real-time connection was interrupted. This can happen when switching between pages quickly.'
                : (this.state.error?.message || 'An unexpected error occurred.')}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Lightweight auth guard for routes that require authentication but NOT
 * membership (e.g. /getting-started, /activate-account). Unlike ProtectedRoute
 * this does not check memberships or permissions.
 */
function AuthOnlyRoute({ children }: { children: ReactNode }) {
  const { user, loading, authError } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Spinner size="lg" label="Loading..." className="min-h-screen" />;
  }

  if (!user || authError) {
    const returnUrl = location.pathname + location.search;
    return (
      <Navigate
        to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}

// Wrapper component to provide PropertyContext inside route tree
function AppRoutes() {
  return (
    <PropertyProvider>
      <NotificationProvider>
      <ScrapingProvider>
      <Suspense fallback={<div className="flex items-center justify-center h-full"><Spinner /></div>}>
      <Routes>
        <Route path="/login" element={withBoundary('Login', <LoginPage />)} />
        <Route path="/pricing" element={withBoundary('Pricing', <PricingPage />)} />
        <Route path="/auth/recover-2fa" element={withBoundary('Recover2FA', <Recover2FAPage />)} />
        <Route path="/getting-started" element={<AuthOnlyRoute>{withBoundary('GettingStarted', <GettingStartedPage />)}</AuthOnlyRoute>} />
        <Route path="/activate-account" element={<AuthOnlyRoute>{withBoundary('AccountActivation', <AccountActivationPage />)}</AuthOnlyRoute>} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* System-wide pages (super admin) */}
          <Route index element={withBoundary('Dashboard', <DashboardPage />)} />
          <Route
            path="organizations"
            element={
              <PermissionGate
                permission="org:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('Organizations', <OrganizationsPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="organizations/:orgId"
            element={
              <PermissionGate
                permission="org:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('Organizations', <OrganizationsPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="organizations/:orgId/structure"
            element={
              <PermissionGate
                permission="org:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('OrganizationStructure', <OrganizationStructurePage />)}
              </PermissionGate>
            }
          />
          <Route
            path="organizations/:orgId/audit"
            element={
              <PermissionGate
                permission="org:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('AuditHistory', <AuditHistoryPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="organizations/:orgId/global-guests"
            element={
              <PermissionGate
                permission="org:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('GlobalGuests', <GlobalGuestsPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="customer-health/:orgId"
            element={
              <PermissionGate
                permission="org:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('CustomerHealthDetail', <CustomerHealthDetailPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties"
            element={
              <PermissionGate
                permission="property:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('Properties', <PropertiesPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="brands"
            element={
              <PermissionGate
                permission="brand:read"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('Brands', <BrandsPage />)}
              </PermissionGate>
            }
          />

          {/* Property-specific pages */}
          <Route path="properties/:propertyId" element={withBoundary('Dashboard', <DashboardPage />)} />
          <Route
            path="properties/:propertyId/sessions"
            element={
              <PermissionGate permission="chat:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('LiveSessions', <LiveSessionsPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/knowledge-base"
            element={
              <PermissionGate permission="kb:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('KnowledgeBase', <KnowledgeBasePage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/handovers"
            element={
              <PermissionGate permission="handover:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('HandoverQueue', <HandoverQueuePage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/analytics"
            element={
              <PermissionGate permission="analytics:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('Analytics', <AnalyticsPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/settings"
            element={
              <PermissionGate permission="property:update" fallback={<Navigate to=".." replace />}>
                {withBoundary('PropertySettings', <PropertySettingsPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/widget-install"
            element={
              <PermissionGate permission="property:update" fallback={<Navigate to=".." replace />}>
                <WidgetInstallPage />
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/guests"
            element={
              <PermissionGate permission="property:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('GuestProfiles', <GuestProfilesPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/automation"
            element={
              <PermissionGate permission="notifications:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('Automation', <AutomationPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/campaigns"
            element={
              <PermissionGate permission="campaigns:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('Campaigns', <CampaignsPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/test-chat"
            element={
              <PermissionGate permission="chat:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('TestChat', <TestChatPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/feedback"
            element={
              <PermissionGate permission="property:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('Feedback', <FeedbackPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/team"
            element={
              <PermissionGate permission="users:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('Team', <TeamPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/billing"
            element={
              <PermissionGate permission="billing:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('Billing', <BillingPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/setup"
            element={
              <PermissionGate permission="property:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('Onboarding', <OnboardingPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="properties/:propertyId/audit"
            element={
              <PermissionGate permission="property:read" fallback={<Navigate to=".." replace />}>
                {withBoundary('AuditHistory', <AuditHistoryPage />)}
              </PermissionGate>
            }
          />
          {/* Legacy onboarding routes — redirect to new structure */}
          <Route path="onboarding" element={<Navigate to="/properties" replace />} />
          <Route
            path="onboarding/:propertyId"
            element={
              <PermissionGate permission="property:read" fallback={<Navigate to="/" replace />}>
                {withBoundary('Onboarding', <OnboardingPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="billing"
            element={
              <PermissionGate
                permission="system:full_access"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('PlatformBilling', <PlatformBillingPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="platform-health"
            element={
              <PermissionGate
                permission="system:full_access"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('PlatformHealth', <PlatformHealthPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="ai-ops"
            element={
              <PermissionGate
                permission="system:full_access"
                fallback={<Navigate to="/" replace />}
              >
                <AIOpsPage />
              </PermissionGate>
            }
          />
          <Route
            path="design-system"
            element={
              <PermissionGate
                permission="system:full_access"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('DesignSystem', <DesignSystemPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="sales-provisioning"
            element={
              <PermissionGate
                permission="system:full_access"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('SalesProvisioning', <SalesProvisioningPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="internal-access"
            element={
              <PermissionGate
                permission="system:full_access"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('InternalAccess', <InternalAccessPage />)}
              </PermissionGate>
            }
          />
          <Route
            path="audit"
            element={
              <PermissionGate
                permission="system:full_access"
                fallback={<Navigate to="/" replace />}
              >
                {withBoundary('AuditHistory', <AuditHistoryPage />)}
              </PermissionGate>
            }
          />

          {/* Account settings (accessible to all authenticated users) */}
          <Route path="account/security" element={withBoundary('SecuritySettings', <SecuritySettingsPage />)} />

          {/* Legacy routes - redirect to new structure */}
          <Route path="sessions" element={<Navigate to="/properties" replace />} />
          <Route path="knowledge-base" element={<Navigate to="/properties" replace />} />
          <Route path="handovers" element={<Navigate to="/properties" replace />} />
          <Route path="analytics" element={<Navigate to="/properties" replace />} />
          <Route path="settings" element={<Navigate to="/properties" replace />} />
        </Route>
      </Routes>
      </Suspense>
      </ScrapingProvider>
      </NotificationProvider>
    </PropertyProvider>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <ToastProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </AppErrorBoundary>
  );
}

export default App;
